import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { ApiClientDto } from "@/features/admin/api/dto";
import { ApiClientRowActions } from "@/features/admin/components/api-clients/api-client-row-actions";
import {
  API_CLIENT_STATUSES,
  type ApiClientStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis. */
export type ApiClientRow = ApiClientDto & { id: string };

const STATUS_VARIANTS: Record<ApiClientStatus, "success" | "destructive"> = {
  ACTIVE: "success",
  REVOKED: "destructive",
};

/**
 * Colonnes des clients API partenaires (`POST /ApiClients/all` — endpoints
 * propres au gateway, §6.1). Filtre select sur `status`, texte sur `name`.
 * Scopes et identifiants en `NoTranslate`.
 */
export const apiClientsColumns: ColumnsClarFactory<ApiClientRow> = () => [
  {
    id: "name",
    accessorKey: "name",
    header: t("admin.apiClients.columns.name"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("admin.apiClients.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as ApiClientStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: API_CLIENT_STATUSES.map((value) => ({ value, label: value })),
    },
  },
  {
    id: "rateLimitPerMinute",
    accessorKey: "rateLimitPerMinute",
    header: t("admin.apiClients.columns.rateLimit"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {String(getValue() ?? "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "scopes",
    accessorKey: "scopes",
    header: t("admin.apiClients.columns.scopes"),
    cell: ({ getValue }) => {
      const scopes = (getValue() as string[] | undefined) ?? [];
      return (
        <NoTranslate as="span" className="flex flex-wrap gap-1">
          {scopes.length
            ? scopes.map((scope) => (
                <Badge key={scope} variant="outline" className="font-mono text-xs">
                  {scope}
                </Badge>
              ))
            : "—"}
        </NoTranslate>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: t("admin.apiClients.columns.createdAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? <ApiClientRowActions apiClient={row.original} /> : null,
    meta: { type: "denied" },
  },
];
